import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft,
  DollarSign,
  Calendar,
  CreditCard,
  Building2,
  User,
  Car,
  Home,
  CheckCircle2,
  AlertCircle,
  Info,
  Calculator,
  Zap,
  Shield,
  Clock,
  TrendingUp,
  Award,
  Target,
  Plus,
  Minus,
  Eye,
  EyeOff,
  ArrowRight,
  Smartphone,
  Wifi,
  Lock,
  Bell,
  Gift,
  Star,
  BarChart3,
  PieChart,
  Receipt,
  Download,
  Share2,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Phone,
  MessageCircle,
  Sun,
  Moon
} from 'lucide-react';

const PayLoan = ({ theme, isDarkMode })  => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showGuidance, setShowGuidance] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [recurringPayment, setRecurringPayment] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(true);
  const [showBalance, setShowBalance] = useState(true);

 
  const loans = [
    {
      id: 1,
      title: 'Personal Loan',
      bank: 'Chase Bank',
      remaining: 15200,
      minPayment: 485,
      dueDate: '2024-12-15',
      rate: 6.5,
      icon: User,
      color: 'bg-blue-500',
      gradient: 'from-blue-500 to-blue-600',
      status: 'current',
      daysPastDue: 0,
      creditImpact: 'positive'
    },
    {
      id: 2,
      title: 'Auto Loan',
      bank: 'Wells Fargo',
      remaining: 28400,
      minPayment: 620,
      dueDate: '2024-12-18',
      rate: 4.2,
      icon: Car,
      color: 'bg-purple-500',
      gradient: 'from-purple-500 to-purple-600',
      status: 'current',
      daysPastDue: 0,
      creditImpact: 'positive'
    },
    {
      id: 3,
      title: 'Home Loan',
      bank: 'Bank of America',
      remaining: 142300,
      minPayment: 1240,
      dueDate: '2024-12-20',
      rate: 3.8,
      icon: Home,
      color: 'bg-emerald-500',
      gradient: 'from-emerald-500 to-emerald-600',
      status: 'current',
      daysPastDue: 0,
      creditImpact: 'positive'
    }
  ];

  const paymentMethods = [
    {
      id: 1,
      type: 'checking',
      name: 'Chase Checking',
      number: '****2847',
      balance: 5420,
      icon: Building2,
      processingTime: 'Instant',
      fee: 0,
      recommended: true
    },
    {
      id: 2,
      type: 'credit',
      name: 'Visa Platinum',
      number: '****4521',
      balance: null,
      icon: CreditCard,
      processingTime: '1-2 days',
      fee: 2.5,
      recommended: false
    },
    {
      id: 3,
      type: 'savings',
      name: 'High Yield Savings',
      number: '****9634',
      balance: 12800,
      icon: PieChart,
      processingTime: '1 day',
      fee: 0,
      recommended: false
    }
  ];

  const quickAmounts = ['Min Payment', '50%', 'Full Amount', 'Custom'];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const calculatePayoffImpact = (amount, loan) => {
    if (!amount || !loan) return null;
    const monthsReduced = Math.floor((amount - loan.minPayment) / 100);
    const interestSaved = monthsReduced * 50;
    return { monthsReduced, interestSaved };
  };

  const getPaymentRecommendation = (loan) => {
    if (!loan) return null;
    const extraPayment = Math.round(loan.minPayment * 0.2);
    return {
      amount: loan.minPayment + extraPayment,
      benefit: 'Saves $420 in interest & 6 months'
    };
  };

  const steps = [
    { number: 1, title: 'Select Loan', icon: Target },
    { number: 2, title: 'Payment Amount', icon: DollarSign },
    { number: 3, title: 'Payment Method', icon: CreditCard },
    { number: 4, title: 'Review & Confirm', icon: CheckCircle2 }
  ];

  const handleLoanSelect = (loan) => {
    setSelectedLoan(loan);
    setPaymentAmount(loan.minPayment.toString());
    setCurrentStep(2);
  };

  const handleAmountSelect = (type) => {
    if (!selectedLoan) return;
    
    switch(type) {
      case 'Min Payment':
        setPaymentAmount(selectedLoan.minPayment.toString());
        break;
      case '50%':
        setPaymentAmount(Math.round(selectedLoan.remaining * 0.5).toString());
        break;
      case 'Full Amount':
        setPaymentAmount(selectedLoan.remaining.toString());
        break;
      default:
        break;
    }
  };

  const processPayment = async () => {
    setIsProcessing(true);
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 3000));
    setIsProcessing(false);
    setShowSuccess(true);
  };

  const resetPayment = () => {
    setCurrentStep(1);
    setSelectedLoan(null);
    setPaymentAmount('');
    setPaymentMethod(null);
    setShowSuccess(false);
    setIsProcessing(false);
  };

  const ProgressBar = () => (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              currentStep >= step.number ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}>
              {currentStep > step.number ? (
                <CheckCircle2 className="w-5 h-5 text-white" />
              ) : (
                <step.icon className={`w-4 h-4 ${
                  currentStep >= step.number ? 'text-white' : 'text-gray-500'
                }`} />
              )}
            </div>
            {index < steps.length - 1 && (
              <div className={`w-8 h-0.5 mx-2 ${
                currentStep > step.number ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
              }`} />
            )}
          </div>
        ))}
      </div>
      <div className="text-center">
        <span className={`text-sm font-medium ${theme.text}`}>
          {steps[currentStep - 1]?.title}
        </span>
      </div>
    </div>
  );

  const SmartGuidance = () => {
    if (!showGuidance) return null;
    
    return (
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border} mb-4`}>
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Info className="w-4 h-4 text-blue-500" />
          </div>
          <div className="flex-1">
            <h4 className={`text-sm font-semibold ${theme.text} mb-1`}>Smart Tip</h4>
            <p className={`text-xs ${theme.textMuted} mb-2`}>
              {currentStep === 1 && "Paying off highest interest rate loans first saves more money."}
              {currentStep === 2 && selectedLoan && `Adding just $${Math.round(selectedLoan.minPayment * 0.2)} extra saves significant interest.`}
              {currentStep === 3 && "Using your checking account provides instant processing with no fees."}
              {currentStep === 4 && "Setting up autopay ensures you never miss a payment."}
            </p>
            <button 
              onClick={() => setShowGuidance(false)}
              className="text-xs text-blue-500 font-medium"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  };

  const Step1LoanSelection = () => (
    <div className="space-y-4">
      <SmartGuidance />
      
      {/* Priority Alert */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border} border-l-4 border-l-amber-500`}>
        <div className="flex items-center space-x-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <span className={`text-sm font-semibold ${theme.text}`}>Payment Priority</span>
        </div>
        <p className={`text-xs ${theme.textMuted}`}>
          Personal Loan has the highest interest rate (6.5%). Consider prioritizing this payment.
        </p>
      </div>

      {/* Loan Cards */}
      <div className="space-y-3">
        {loans.map((loan) => {
          const daysUntilDue = Math.ceil((new Date(loan.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
          return (
            <button
              key={loan.id}
              onClick={() => handleLoanSelect(loan)}
              className={`w-full ${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border} text-left hover:border-blue-500 transition-all group`}
            >
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 ${loan.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                  <loan.icon className="w-6 h-6 text-white" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className={`font-semibold ${theme.text} text-sm`}>{loan.title}</h3>
                      <p className={`text-xs ${theme.textMuted}`}>{loan.bank}</p>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-bold ${theme.text}`}>{formatCurrency(loan.remaining)}</div>
                      <div className={`text-xs ${theme.textMuted}`}>remaining</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div>
                        <div className={`text-xs ${theme.textMuted}`}>Min Payment</div>
                        <div className={`text-sm font-semibold ${theme.warning}`}>{formatCurrency(loan.minPayment)}</div>
                      </div>
                      <div>
                        <div className={`text-xs ${theme.textMuted}`}>Due in</div>
                        <div className={`text-sm font-semibold ${
                          daysUntilDue <= 3 ? theme.error : daysUntilDue <= 7 ? theme.warning : theme.success
                        }`}>{daysUntilDue} days</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <div className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-500 rounded-full">
                        {loan.rate}% APR
                      </div>
                      <ArrowRight className={`w-4 h-4 ${theme.textMuted} group-hover:text-blue-500 transition-colors`} />
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const Step2AmountSelection = () => {
    const impact = calculatePayoffImpact(parseInt(paymentAmount), selectedLoan);
    const recommendation = getPaymentRecommendation(selectedLoan);
    
    return (
      <div className="space-y-4">
        <SmartGuidance />
        
        {/* Selected Loan Summary */}
        <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
          <div className="flex items-center space-x-3 mb-3">
            <div className={`w-10 h-10 ${selectedLoan.color} rounded-xl flex items-center justify-center`}>
              <selectedLoan.icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className={`font-semibold ${theme.text}`}>{selectedLoan.title}</h3>
              <p className={`text-xs ${theme.textMuted}`}>Balance: {formatCurrency(selectedLoan.remaining)}</p>
            </div>
          </div>
          
          {/* Recommendation */}
          {recommendation && (
            <div className="bg-blue-500/10 rounded-lg p-3 mb-3">
              <div className="flex items-center space-x-2 mb-1">
                <Award className="w-4 h-4 text-blue-500" />
                <span className={`text-sm font-semibold ${theme.text}`}>Smart Recommendation</span>
              </div>
              <p className={`text-xs ${theme.textMuted} mb-2`}>
                Pay {formatCurrency(recommendation.amount)} • {recommendation.benefit}
              </p>
              <button
                onClick={() => setPaymentAmount(recommendation.amount.toString())}
                className="text-xs text-blue-500 font-medium"
              >
                Use This Amount
              </button>
            </div>
          )}
        </div>

        {/* Quick Amount Buttons */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {quickAmounts.map((amount) => (
            <button
              key={amount}
              onClick={() => handleAmountSelect(amount)}
              className={`p-3 rounded-xl border ${theme.border} ${theme.cardBg} backdrop-blur-xl hover:border-blue-500 transition-all`}
            >
              <div className={`text-sm font-medium ${theme.text} mb-1`}>{amount}</div>
              {amount !== 'Custom' && (
                <div className={`text-xs ${theme.textMuted}`}>
                  {amount === 'Min Payment' && formatCurrency(selectedLoan.minPayment)}
                  {amount === '50%' && formatCurrency(Math.round(selectedLoan.remaining * 0.5))}
                  {amount === 'Full Amount' && formatCurrency(selectedLoan.remaining)}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Custom Amount Input */}
        <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
          <label className={`block text-sm font-medium ${theme.text} mb-2`}>Payment Amount</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <DollarSign className={`w-5 h-5 ${theme.textMuted}`} />
            </div>
            <input
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 ${theme.cardBg} border ${theme.border} rounded-xl ${theme.text} focus:border-blue-500 focus:outline-none`}
              placeholder="Enter amount"
            />
          </div>
          
          {/* Amount Validation */}
          {parseInt(paymentAmount) < selectedLoan.minPayment && paymentAmount && (
            <div className="flex items-center space-x-2 mt-2 text-red-500">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs">Amount must be at least {formatCurrency(selectedLoan.minPayment)}</span>
            </div>
          )}
        </div>

        {/* Payment Impact */}
        {impact && parseInt(paymentAmount) > selectedLoan.minPayment && (
          <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
            <h4 className={`text-sm font-semibold ${theme.text} mb-3`}>Payment Impact</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className={`text-lg font-bold ${theme.success}`}>{impact.monthsReduced}</div>
                <div className={`text-xs ${theme.textMuted}`}>Months Saved</div>
              </div>
              <div className="text-center">
                <div className={`text-lg font-bold ${theme.success}`}>{formatCurrency(impact.interestSaved)}</div>
                <div className={`text-xs ${theme.textMuted}`}>Interest Saved</div>
              </div>
            </div>
          </div>
        )}

        {/* Advanced Options */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={`w-full flex items-center justify-between p-3 ${theme.cardBg} backdrop-blur-xl rounded-xl border ${theme.border}`}
        >
          <span className={`text-sm font-medium ${theme.text}`}>Advanced Options</span>
          {showAdvanced ? 
            <ChevronUp className={`w-4 h-4 ${theme.textMuted}`} /> : 
            <ChevronDown className={`w-4 h-4 ${theme.textMuted}`} />
          }
        </button>

        {showAdvanced && (
          <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border} space-y-4`}>
            {/* Schedule Payment */}
            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-2`}>Schedule Payment</label>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}
              />
            </div>

            {/* Recurring Payment */}
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-sm font-medium ${theme.text}`}>Set up Recurring</div>
                <div className={`text-xs ${theme.textMuted}`}>Automate future payments</div>
              </div>
              <button
                onClick={() => setRecurringPayment(!recurringPayment)}
                className={`w-12 h-6 rounded-full relative transition-colors ${
                  recurringPayment ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${
                  recurringPayment ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>
        )}

        <button
          onClick={() => setCurrentStep(3)}
          disabled={!paymentAmount || parseInt(paymentAmount) < selectedLoan.minPayment}
          className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 rounded-xl font-medium transition-colors"
        >
          Continue to Payment Method
        </button>
      </div>
    );
  };

  const Step3PaymentMethod = () => (
    <div className="space-y-4">
      <SmartGuidance />
      
      {/* Payment Summary */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h3 className={`text-sm font-semibold ${theme.text} mb-3`}>Payment Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className={`text-sm ${theme.textMuted}`}>Loan:</span>
            <span className={`text-sm ${theme.text}`}>{selectedLoan.title}</span>
          </div>
          <div className="flex justify-between">
            <span className={`text-sm ${theme.textMuted}`}>Amount:</span>
            <span className={`text-sm font-semibold ${theme.text}`}>{formatCurrency(parseInt(paymentAmount))}</span>
          </div>
          {scheduledDate && (
            <div className="flex justify-between">
              <span className={`text-sm ${theme.textMuted}`}>Scheduled:</span>
              <span className={`text-sm ${theme.text}`}>{formatDate(scheduledDate)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Payment Methods */}
      <div className="space-y-3">
        <h3 className={`text-sm font-semibold ${theme.text}`}>Select Payment Method</h3>
        
        {paymentMethods.map((method) => (
          <button
            key={method.id}
            onClick={() => setPaymentMethod(method)}
            className={`w-full ${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border transition-all ${
              paymentMethod?.id === method.id ? 'border-blue-500 bg-blue-500/5' : theme.border
            }`}
          >
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                method.recommended ? 'bg-emerald-500' : 'bg-gray-500'
              }`}>
                <method.icon className="w-6 h-6 text-white" />
              </div>
              
              <div className="flex-1 text-left">
                <div className="flex items-center space-x-2 mb-1">
                  <span className={`font-medium ${theme.text}`}>{method.name}</span>
                  {method.recommended && (
                    <span className="text-xs bg-emerald-500/20 text-emerald-500 px-2 py-1 rounded-full">
                      Recommended
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-4 text-xs">
                  <span className={theme.textMuted}>{method.number}</span>
                  {method.balance && (
                    <span className={theme.textMuted}>
                      Balance: {showBalance ? formatCurrency(method.balance) : '••••••'}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-4 mt-1">
                  <span className={`text-xs ${method.fee > 0 ? theme.warning : theme.success}`}>
                    {method.fee > 0 ? `${method.fee}% fee` : 'No fees'}
                  </span>
                  <span className={`text-xs ${theme.textMuted}`}>{method.processingTime}</span>
                </div>
              </div>
              
              <div className="flex flex-col items-center space-y-2">
                {paymentMethod?.id === method.id && (
                  <CheckCircle2 className="w-5 h-5 text-blue-500" />
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Security Features */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="flex items-center space-x-2 mb-3">
          <Shield className="w-4 h-4 text-emerald-500" />
          <span className={`text-sm font-semibold ${theme.text}`}>Security Features</span>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Lock className="w-4 h-4 text-emerald-500" />
              <span className={`text-sm ${theme.text}`}>256-bit Encryption</span>
            </div>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Smartphone className="w-4 h-4 text-emerald-500" />
              <span className={`text-sm ${theme.text}`}>Biometric Authentication</span>
            </div>
            <button
              onClick={() => setBiometricEnabled(!biometricEnabled)}
              className={`w-10 h-5 rounded-full relative transition-colors ${
                biometricEnabled ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-transform ${
                biometricEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>

      <button
        onClick={() => setCurrentStep(4)}
        disabled={!paymentMethod}
        className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 rounded-xl font-medium transition-colors"
      >
        Review Payment
      </button>
    </div>
  );

  const Step4Review = () => {
    const fee = paymentMethod?.fee > 0 ? (parseInt(paymentAmount) * paymentMethod.fee / 100) : 0;
    const total = parseInt(paymentAmount) + fee;
    
    return (
      <div className="space-y-4">
        {/* Final Review */}
        <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
          <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Review Payment</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-200/20">
              <span className={`${theme.textMuted}`}>Loan:</span>
              <span className={`font-medium ${theme.text}`}>{selectedLoan.title}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-200/20">
              <span className={`${theme.textMuted}`}>Payment Amount:</span>
              <span className={`font-semibold ${theme.text}`}>{formatCurrency(parseInt(paymentAmount))}</span>
            </div>
            {fee > 0 && (
              <div className="flex items-center justify-between py-2 border-b border-gray-200/20">
                <span className={`${theme.textMuted}`}>Processing Fee:</span>
                <span className={`font-medium ${theme.warning}`}>{formatCurrency(fee)}</span>
              </div>
            )}
            <div className="flex items-center justify-between py-2 border-b border-gray-200/20">
              <span className={`${theme.textMuted}`}>Payment Method:</span>
              <span className={`font-medium ${theme.text}`}>{paymentMethod.name} {paymentMethod.number}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-200/20">
              <span className={`${theme.textMuted}`}>Processing Time:</span>
              <span className={`font-medium ${theme.text}`}>{paymentMethod.processingTime}</span>
            </div>
            {scheduledDate && (
              <div className="flex items-center justify-between py-2 border-b border-gray-200/20">
                <span className={`${theme.textMuted}`}>Scheduled Date:</span>
                <span className={`font-medium ${theme.text}`}>{formatDate(scheduledDate)}</span>
              </div>
            )}
            {recurringPayment && (
              <div className="flex items-center justify-between py-2 border-b border-gray-200/20">
                <span className={`${theme.textMuted}`}>Recurring:</span>
                <span className={`font-medium ${theme.success}`}>Monthly</span>
              </div>
            )}
            <div className="flex items-center justify-between py-3">
              <span className={`text-lg font-semibold ${theme.text}`}>Total:</span>
              <span className={`text-xl font-bold ${theme.text}`}>{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        {/* Impact Summary */}
        <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
          <h4 className={`text-sm font-semibold ${theme.text} mb-3`}>Payment Impact</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className={`text-lg font-bold ${theme.success}`}>
                {formatCurrency(selectedLoan.remaining - parseInt(paymentAmount))}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>New Balance</div>
            </div>
            <div className="text-center">
              <div className={`text-lg font-bold ${theme.accent}`}>
                {formatDate(selectedLoan.dueDate)}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>Next Due Date</div>
            </div>
          </div>
        </div>

        {/* Security Confirmation */}
        <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
          <div className="flex items-center space-x-3 mb-3">
            <Shield className="w-5 h-5 text-emerald-500" />
            <span className={`text-sm font-semibold ${theme.text}`}>Security Verification</span>
          </div>
          <p className={`text-xs ${theme.textMuted} mb-3`}>
            This payment will be processed securely with bank-level encryption. 
            {biometricEnabled && " Biometric verification will be required."}
          </p>
          <div className="flex items-center space-x-2">
            <Lock className="w-4 h-4 text-emerald-500" />
            <span className={`text-xs ${theme.success}`}>Payment secured with 256-bit SSL encryption</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={processPayment}
            disabled={isProcessing}
            className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 text-white py-4 rounded-xl font-semibold text-lg transition-colors flex items-center justify-center space-x-2"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>Processing Payment...</span>
              </>
            ) : (
              <>
                <Lock className="w-5 h-5" />
                <span>Confirm & Pay {formatCurrency(total)}</span>
              </>
            )}
          </button>
          
          <button
            onClick={() => setCurrentStep(3)}
            disabled={isProcessing}
            className={`w-full py-3 rounded-xl font-medium transition-colors ${theme.cardBg} border ${theme.border} ${theme.text} hover:border-blue-500`}
          >
            Back to Payment Method
          </button>
        </div>
      </div>
    );
  };

  const ProcessingScreen = () => (
    <div className="text-center space-y-6">
      <div className="relative">
        <div className="w-20 h-20 mx-auto bg-blue-500 rounded-full flex items-center justify-center animate-pulse">
          <DollarSign className="w-10 h-10 text-white" />
        </div>
        <div className="absolute inset-0 w-20 h-20 mx-auto border-4 border-blue-200 rounded-full animate-spin border-t-blue-500"></div>
      </div>
      
      <div>
        <h3 className={`text-xl font-semibold ${theme.text} mb-2`}>Processing Payment</h3>
        <p className={`${theme.textMuted}`}>Please don't close the app while we process your payment</p>
      </div>
      
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="space-y-2 text-sm">
          <div className="flex items-center space-x-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span className={theme.textMuted}>Verifying payment details</span>
          </div>
          <div className="flex items-center space-x-2">
            <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
            <span className={theme.text}>Processing with {paymentMethod.name}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className={theme.textMuted}>Updating loan balance</span>
          </div>
        </div>
      </div>
    </div>
  );

  const SuccessScreen = () => (
    <div className="text-center space-y-6">
      {/* Success Animation */}
      <div className="relative">
        <div className="w-20 h-20 mx-auto bg-emerald-500 rounded-full flex items-center justify-center animate-bounce">
          <CheckCircle2 className="w-10 h-10 text-white" />
        </div>
        <div className="absolute inset-0 w-20 h-20 mx-auto border-4 border-emerald-200 rounded-full animate-ping"></div>
      </div>
      
      <div>
        <h3 className={`text-2xl font-bold ${theme.text} mb-2`}>Payment Successful!</h3>
        <p className={`${theme.textMuted}`}>Your payment of {formatCurrency(parseInt(paymentAmount))} has been processed</p>
      </div>

      {/* Transaction Details */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border} text-left`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-3`}>Transaction Details</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className={theme.textMuted}>Transaction ID:</span>
            <span className={`font-mono ${theme.text}`}>TXN-{Date.now().toString().slice(-8)}</span>
          </div>
          <div className="flex justify-between">
            <span className={theme.textMuted}>Date & Time:</span>
            <span className={theme.text}>{new Date().toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className={theme.textMuted}>New Balance:</span>
            <span className={`font-semibold ${theme.success}`}>
              {formatCurrency(selectedLoan.remaining - parseInt(paymentAmount))}
            </span>
          </div>
        </div>
      </div>

      {/* Credit Score Impact */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="flex items-center space-x-2 mb-2">
          <TrendingUp className="w-4 h-4 text-emerald-500" />
          <span className={`text-sm font-semibold ${theme.text}`}>Credit Score Impact</span>
        </div>
        <p className={`text-xs ${theme.textMuted} mb-2`}>
          On-time payments help improve your credit score
        </p>
        <div className="flex items-center space-x-2">
          <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div className="bg-emerald-500 h-2 rounded-full w-3/4"></div>
          </div>
          <span className={`text-xs ${theme.success}`}>+2 points</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <button className={`flex flex-col items-center space-y-2 p-4 ${theme.cardBg} border ${theme.border} rounded-xl hover:border-blue-500 transition-all`}>
          <Receipt className="w-6 h-6 text-blue-500" />
          <span className={`text-xs font-medium ${theme.text}`}>Download Receipt</span>
        </button>
        
        <button className={`flex flex-col items-center space-y-2 p-4 ${theme.cardBg} border ${theme.border} rounded-xl hover:border-blue-500 transition-all`}>
          <Share2 className="w-6 h-6 text-blue-500" />
          <span className={`text-xs font-medium ${theme.text}`}>Share Confirmation</span>
        </button>
      </div>

      {/* Rewards Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Gift className="w-6 h-6 text-white" />
            <div className="text-left">
              <div className="text-white font-semibold text-sm">Earn Rewards!</div>
              <div className="text-purple-100 text-xs">Set up autopay & earn 1% cashback</div>
            </div>
          </div>
          <button className="bg-white/20 backdrop-blur text-white px-3 py-2 rounded-lg font-medium text-xs hover:bg-white/30 transition-colors">
            Enable
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={resetPayment}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-xl font-semibold transition-colors"
        >
          Make Another Payment
        </button>
        
        <button
          onClick={() => window.history.back()}
          className={`w-full py-3 rounded-xl font-medium transition-colors ${theme.cardBg} border ${theme.border} ${theme.text} hover:border-blue-500`}
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );

    const SupportBar = () => (
    <div className="fixed bottom-14 right-2 z-50">
        <div className="flex flex-col space-y-1 sm:space-y-2">

        {/* Chat Button */}
        <button className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-500 rounded-full flex items-center justify-center shadow-sm hover:scale-105 transition-all">
            <MessageCircle className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-white" />
        </button>

        {/* Call Button */}
        <button className="w-9 h-9 sm:w-10 sm:h-10 bg-emerald-500 rounded-full flex items-center justify-center shadow-sm hover:scale-105 transition-all">
            <Phone className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-white" />
        </button>

        {/* Help Button */}
        <button className="w-9 h-9 sm:w-10 sm:h-10 bg-amber-500 rounded-full flex items-center justify-center shadow-sm hover:scale-105 transition-all">
            <HelpCircle className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-white" />
        </button>

        </div>
    </div>
    );


  return (
    <div className={`min-h-screen ${theme.bg} transition-all duration-300`}>
      {/* Header */}
    <div className="flex items-center justify-between p-3 pt-8 sticky top-0 z-40 backdrop-blur-xl">
    <div className="flex items-center space-x-2 sm:space-x-3">
        
        {/* Back Button */}
        <button 
        onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : window.history.back()}
        className={`w-8 h-8 sm:w-10 sm:h-10 ${theme.cardBg} border ${theme.border} rounded-lg flex items-center justify-center backdrop-blur-xl`}
        >
        <ArrowLeft className={`w-4 h-4 sm:w-5 sm:h-5 ${theme.text}`} />
        </button>

        <div>
        <h1 className={`text-lg sm:text-xl font-bold ${theme.text}`}>Pay Loan</h1>
        <p className={`text-xs ${theme.textMuted}`}>
            {showSuccess ? 'Payment Complete' : isProcessing ? 'Processing...' : `Step ${currentStep} of 4`}
        </p>
        </div>
    </div>
    
    </div>


      {/* Main Content */}
      <div className="px-4 pb-8 max-w-md mx-auto lg:max-w-2xl lg:px-8">
        {!showSuccess && !isProcessing && <ProgressBar />}
        
        {isProcessing && <ProcessingScreen />}
        {showSuccess && <SuccessScreen />}
        
        {!isProcessing && !showSuccess && (
          <>
            {currentStep === 1 && <Step1LoanSelection />}
            {currentStep === 2 && <Step2AmountSelection />}
            {currentStep === 3 && <Step3PaymentMethod />}
            {currentStep === 4 && <Step4Review />}
          </>
        )}
      </div>

      {/* Support Bar */}
      <SupportBar />
    </div>
  );
};

export default PayLoan;